import React, { useState, useCallback, useReducer } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, getPromotionMainColor, getNumTries, isEmpty, isNotEmpty } from '../../lib/ep.js';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';

import ActionButton, { CreateButton } from '../builder/ActionButton.js';
import ActionLink from '../builder/ActionLink.js';
import { AutoPostImageUploader } from '../builder/ImageUploader.js';
import BuilderCanvas from '../builder/BuilderCanvas.js';
import BuilderSidebar from '../builder/BuilderSidebar.js';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import DisplayList from '../builder/DisplayList.js';
import Message, { AlertMessage } from '../builder/Message.js';
import Model from '../builder/Model.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs.js';
import Select from '../builder/Select.js';
import SectionSubtitle from '../builder/SectionSubtitle.js';
import SectionTitle from '../builder/SectionTitle.js';
import BuilderMain, { BuilderMainWithoutSidebar } from '../builder/BuilderMain.js';

import Layout from '../front/Layout.js';

import RevealStage, { InstructionsLayer } from '../../containers/stages/RevealStage.js';
import Input from '../builder/Input.js';
import { OddsLayer } from './RouletteBuilder.js';
import { PrizeSubtitle } from './ScratchBuilder.js';
import DisabledArea from '../builder/DisabledArea.js';
import RichEditor from '../builder/RichEditor.js';

import transferPlaceholder from '../../hoc/transferPlaceholder.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import GameSoundtrackFields from '../builder/GameSoundtrackFields.js';

const RichEditorPlaceHolder = injectIntl(transferPlaceholder(RichEditor));
const InputPlaceHolder = injectIntl(transferPlaceholder(Input));

const default_images_path = `${window.static_domain_1}img/games_gui/reveal/`;

const RevealCanvasInBuilder = ({ stage, state, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	const is_test = props?.is_test || false;
	const is_editing = (!is_test && props.is_editing != undefined) ? props.is_editing : false;
	const is_enabled = props?.is_enabled || true;
	
	if(!is_enabled) {
		return (
			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promo_menu} layout_extra_classes="is_new_game">
					<div style={{width:'100%', height:'100%'}}>
						<img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" />
						<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
							<p>{i18n("Please, complete all the game options on the left")}</p>
						</div>
					</div>
				</Layout>
			</BuilderCanvas>
		)
	}

	const areaChanged = (type, area) => {
		if(is_test) return;

		switch(type) {
			case 'full':
				change('reveal_individual_area',area);
				break;

			case 'group':
				change('reveal_group_area', area);
				break;

			default:
				change('reveal_individual_area', area)
				break;
		}
	}

	const display_instructions = props?.display_instructions ?? false;

	if(display_instructions) {
		return (
			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promo_menu} layout_extra_classes="is_new_game" only_contents={true} without_context={true} hide_wrapper={true}>
					<InstructionsLayer
						stage={stage}
						promo={props.promotion}
						is_builder={true}
						intl={props.intl}
					/>
				</Layout>
			</BuilderCanvas>
		)
	}

	return (
		<BuilderCanvas fit_to_window="content">
			<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promo_menu} layout_extra_classes="is_new_game">
				<RevealStage
					stage={{
						Stage:{
							...stage,
							reveal_win:state.reveal_win,
							scratch:state.scratch
						}
					}}
					is_builder={true}
					is_editing={is_editing}
					is_playable={is_test}
					prizes={props.prizes}
					given_prize={props.preview}
					promotion={{Promotion:props.promotion}}
					areaChanged={areaChanged}
					display_instructions={display_instructions}
				/>
			</Layout>
		</BuilderCanvas>
	)
};

const RevealPrizes = ({ stage, state, dispatch, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ odds_layer, setOddsLayer ] = useState(false);

	const dynamic_fields = [{
		type:'scratch_prize',
		label:false,
		title:(f) => {
			if(f.id == 0) return i18n("No prize");
			const p = props.prizes.find((p) => p.id == f.id);
			const title_parts = [];
			if(p.title != undefined) title_parts.push(p.title);
			if(isNotEmpty(p, 'reference')) title_parts.push(`(${p.reference})`);
			if(title_parts.length == 0) return "";
			return title_parts.join(" ");
		},
	}];

	const saveStage = useCallback((cb) => {
		change('scratch',state.scratch);
		change('reveal_win',state.reveal_win);
		props.saveStage(cb);
	}, [state.scratch, state.reveal_win]);

	const instants = props.instantStore.getState().instant.instants;
	let instantCount = {};
	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] != undefined) ? (instantCount[instant.prize_type] + 1) : 1;
	});

	const num_options = [{ k:1, v:1 }, { k:3, v:3 }];

	let size_options = [];
	switch(stage.reveal_num) {
		case '1':
			size_options = [2, 3, 4, 5].map((i) => ({ k:i, v:i }));
			break;

		case '3':
			size_options = [
				{ k:3, v:`9 ${i18n("items")} (3×3)` },
				{ k:4, v:`16 ${i18n("items")} (4×4)` },
				{ k:5, v:`25 ${i18n("items")} (5×5)` },
			];
			break;
	}
	size_options.unshift({ k:'', v:`-- ${i18n("Choose a number of items")} --` });

	const tries_options = getNumTries(stage.reveal_num, stage.reveal_size);

	const is_reveal_size_disabled = (stage.reveal_num == undefined || isEmpty(stage, 'reveal_mode')) ? true : false;
	const is_reveal_tries_disabled = (is_reveal_size_disabled || isEmpty(stage, 'reveal_size')) ? true : false;

	return (
		<>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							_class={{ size:30, first:true }}
							name="reveal_num"
							values={num_options}
						>Prizes to reveal</Select>
					</Model>

					<DisabledArea disabled={is_reveal_size_disabled}>
						<Model model={stage} change={change}>
							<Select
								name="reveal_size"
								values={size_options}
							>Items on the grid</Select>
						</Model>
					</DisabledArea>

					<DisabledArea disabled={is_reveal_tries_disabled}>
						<Model model={stage} change={change}>
							<Select
								name="reveal_tries"
								_class={{ size:30 }}
								values={tries_options}
							>Number of attempts per game</Select>
						</Model>
					</DisabledArea>
				</article>

				<article class="gui_not_in_top_huge">
					{(stage.reveal_mode == 'prizes' && state.scratch.length < 2) && (
						<AlertMessage
							title="No prizes"
						>
							<p>{i18n("There is no prize.")} <ActionLink _href="#prizes">{i18n("Create first prize")}</ActionLink></p>
						</AlertMessage>
					)}

					{(stage.roulette_odds == undefined) && (
						<AlertMessage
							title="No odds"
						>
							<p>{i18n("The probability of awarding prizes has not been defined.")} <ActionLink onClick={() => setOddsLayer(true)}>Define odds now</ActionLink></p>
						</AlertMessage>
					)}

					<DisplayList
						onChange={(v) => dispatch({ type:'CHANGE_REVEAL_PRIZES', v })}
						field_types={dynamic_fields}
						fields={state.scratch}
						actions={[]}
						section_title={i18n("Prizes")}
						title_action={<ActionLink
							onClick={() => setOddsLayer(true)}
						>Configure prize odds</ActionLink>}
						subtitle={<PrizeSubtitle
							stage={stage}
							prizes={props.prizes}
							intl={props.intl}
						/>}
					/>

					{(odds_layer) && (
						<OddsLayer
							close={() => setOddsLayer(false)}
							max_odds_summ={1000}
							stage={stage}
							has_no_prize_slices={true}
							has_prizes_iw={false}
							prizes={props.prizes}
							change={change}
							on_save={props.on_save}
						/>
					)}
				</article>
			</BuilderSidebar>

			<RevealCanvasInBuilder
				change={change}
				stage={stage}
				state={state}
				promotion={props.promotion}
				prizes={props.prizes}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				is_editing={false}
				is_enabled={!is_reveal_size_disabled && !is_reveal_tries_disabled}
			/>
		</>
	)
};

const RevealInstructions = ({ stage, change,state,preview, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_title_label = "How to play?";

	const start_button_label = i18n("Label for '%s' button").replace('%s', i18n("Start"));

	const description_placeholder = props.intl.formatMessage({ id:"You have {tries} attempts to find {num_prizes} prizes" }, { tries:stage.reveal_tries, num_prizes:stage.reveal_num })

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		small_input:true,
		_size:{
			width:600,
			height:600,
		},
	};

	return (
		<>
			<BuilderSidebar onSave={props.saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props.nav_sections || []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<Model model={stage} change={change}>
					<InputPlaceHolder _class={{first:true, size:80}} name="instructions_title" max_length={30} placeholder={default_title_label}>Title</InputPlaceHolder>

					<AutoPostImageUploader image={stage.full_instructions_img} name='instructions_img' {...autouploader_props}>Image</AutoPostImageUploader>

					<RichEditorPlaceHolder name="instructions_description" placeholder={description_placeholder} has_emojis={true}>Description</RichEditorPlaceHolder>

					<InputPlaceHolder name="instructions_button" placeholder="Try your luck" autotranslate={false}>{start_button_label}</InputPlaceHolder>
				</Model>
			</BuilderSidebar>

			<RevealCanvasInBuilder
				change={change}
				stage={stage}
				state={state}
				promotion={props.promotion}
				prizes={props.prizes}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				preview={preview}
				display_instructions={true}
			/>
		</>
	)
}

const RevealDesign = ({ stage, change, state, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const saveStage = useCallback((cb) => {
		change('scratch', state.scratch);
		change('reveal_win', state.reveal_win);
		props.saveStage(cb);
	}, [state.scratch, state.reveal_win]);

	const instants = props.instantStore.getState().instant.instants;
	let instantCount = {};
	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] != undefined) ? (instantCount[instant.prize_type] + 1) : 1;
	});

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		small_input:true,
		_size:{
			width:600,
			height:600,
		},
	};
	
	const transition_options = [
		{ k:'fade_in', v:i18n("Fade in") },
		{ k:'direct', v:i18n("Direct") },
		{ k:'scratch', v:i18n("Scratch") },
	];

	const position_options = [
		{ k:'block', v:i18n("Grouped") },
		{ k:'manual', v:i18n("Individual items") },
	];

	return (
		<>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={props.nav_active}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<Select
							_class={{size:50, first:true}}
							name="reveal_transition"
							values={transition_options}
						>Revealing transition</Select>

						<Select
							_class={{size:60}}
							value={stage.reveal_pos_mode ?? ((stage.reveal_individual_area == undefined) ? 'block' : 'manual')}
							name="reveal_pos_mode"
							values={position_options}
						>Positioning</Select>

						<ColorPicker
							_class={{size:60}}
							_hide_code={true}
							name="bg_color"
							color={stage?.bg_color ?? getPromotionMainColor(props.promotion)}
						>Background color</ColorPicker>

						<ColorPicker
							_class={{size:60}}
							_hide_code={true}
							name="game_color_ok"
							color={stage?.game_color_ok ?? '#75C400'}
						>Hit color</ColorPicker>

						<ColorPicker
							_class={{size:60}}
							_hide_code={true}
							name="game_color_ko"
							color={stage?.game_color_ko ?? '#D8253B'}
						>Fail color</ColorPicker>

						<AutoPostImageUploader
							image={(stage.full_bg_image != "") ? stage.full_bg_image : undefined}
							name='bg_image'
							{...autouploader_props}
						>Background image</AutoPostImageUploader>

						<Checkbox
							name="silence"
						>Disable sounds</Checkbox>
					</Model>

					<GameSoundtrackFields
						stage={stage}
						change={change}
					/>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Result screen"
						icon='knk-logout'
					/>

					<Model model={stage} change={change}>
						<AutoPostImageUploader
							image={(stage.bg_image_ok != "") ? stage.full_bg_image_ok : undefined}
							name='bg_image_ok'
							{...autouploader_props}
						>Background image when WIN</AutoPostImageUploader>

						<AutoPostImageUploader
							image={(stage.full_bg_image_ko != "") ? stage.full_bg_image_ko : undefined}
							name='bg_image_ko'
							{...autouploader_props}
						>Background image when LOSE</AutoPostImageUploader>

						<Checkbox
							name='reveal_hide_final'
						>Hide items to show only the background image</Checkbox>

						<InputPlaceHolder
							_class={{size:50}}
							name="reveal_message_ok"
							placeholder="YOU WIN"
							max_length={10}
						>Message for winners</InputPlaceHolder>

						<InputPlaceHolder
							_class={{size:50}}
							name="reveal_message_ko"
							placeholder="Oops"
							max_length={10}
						>Message for non-winners</InputPlaceHolder>
					</Model>
				</article>
			</BuilderSidebar>

			<RevealCanvasInBuilder
				change={change}
				stage={stage}
				state={state}
				promotion={props.promotion}
				prizes={props.prizes}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				is_editing={true}
			/>
		</>
	)
};

const RevealImages = ({ stage, change, dispatch, state, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const saveStage = useCallback((cb) => {
		change('scratch', state.scratch);
		change('reveal_win', state.reveal_win);
		props.saveStage(cb);
	}, [state.scratch, state.reveal_win]);

	const instants = props.instantStore.getState().instant.instants;
	let instantCount = {};
	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] != undefined) ? (instantCount[instant.prize_type] + 1) : 1;
	});

	const autouploader_props = {
		auto_url:`/promotions/auto_img/${stage.promotion_id}`,
		auto_type:'stages',
		display_custom_dragging_instructions:"Upload the image",
		thumb_w:80,
		_size:{
			width:120,
			height:120,
		},
	};
	
	const prizes_to_images = (stage.reveal_mode == 'win') ? state.reveal_win : state.scratch;

	const change_prize = (id,k,v) => {
		let new_data;

		if (stage.reveal_mode == 'win' || id == 0) {
			for(var i in stage.reveal_win) {
				if(stage.reveal_win[i].id != id) continue;
				stage.reveal_win[i] = { ...stage.reveal_win[i], [k]:v ,type:'reveal_win'};	
				new_data = [...stage.reveal_win];
				break;
			}
			dispatch({ type:'CHANGE_REVEAL_WIN',v:new_data });
		}

		if (stage.reveal_mode == 'prizes' || id == 0) {
			for(var i in stage.scratch) {
				if(stage.scratch[i].id != id) continue;
				stage.scratch[i] = { ...stage.scratch[i], [k]:v,type:'scratch_prize'};	
				new_data = [...stage.scratch];
				break;
			}
			dispatch({ type:'CHANGE_REVEAL_PRIZES', v:new_data });
		}
	};

	const mode_options = [
		{ k:'win', v:i18n("Same image for all prizes") },
		{ k:'prizes', v:i18n("Different image for each prize") },
	];

	const no_prize = prizes_to_images.find(i=>(i.id ==0));

	return (
		<>
			<BuilderSidebar onSave={saveStage}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props.nav_sections || []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={change}>
						<AutoPostImageUploader _class={{first:true}} image={stage.full_scratch_cover} name='scratch_cover' auto_type='stages' {...autouploader_props}>Image for the items before reveal</AutoPostImageUploader>
					</Model>

					<Model model={no_prize} change={(k,v)=>change_prize(0,k,v)}>
						<AutoPostImageUploader image={no_prize.full_scratch_image} name='scratch_image' auto_type='stages' {...autouploader_props}>Image for the items without a prize</AutoPostImageUploader>
					</Model>

					<Model model={stage} change={change}>
						<Select name="reveal_mode" values={mode_options}>Image for the items with a prize</Select>
					</Model>

					{prizes_to_images.map((pr) => {
						if(pr.id == 0) return null;

						const actual_pt = (stage.reveal_mode != 'win') ? props.prizes.find((p) => { return p.id == pr.id }).title : i18n("Prize");

						return (
							<Model key={`pr_${pr.id}`} model={pr} change={(k,v)=>change_prize(pr.id,k,v)}>
								<AutoPostImageUploader image={pr.full_scratch_image} name='scratch_image' auto_type='stages' {...autouploader_props} autotranslate={false}><FormattedMessage id="Image for {prize}" values={{prize:`"${actual_pt}"`}} /></AutoPostImageUploader>
							</Model>
						)
					})}
				</article>
			</BuilderSidebar>

			<RevealCanvasInBuilder
				change={change}
				stage={stage}
				state={state}
				promotion={props.promotion}
				prizes={props.prizes}
				promo_menu={props.promoModel.Custom__Menus}
				intl={props.intl}
				is_editing={true}
			/>
		</>
	)
};

const RevealTest = ({ stage, state, dispatch, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);
	
	const [preview, setPreview] = useState(false);

	const changePreview = (k, v) => {
		resetGame(v);
	};

	const resetGame = (id) => {
		setPreview(-1);
		setTimeout(() => {
			setPreview((id != undefined) ? id : preview);
		}, 0);
	};

	const instants = props.instantStore.getState().instant.instants;
	let instantCount = {};
	instants.map((instant) => {
		instantCount[instant.prize_type] = (instantCount[instant.prize_type] == undefined) ? 1 : instantCount[instant.prize_type] + 1;
	});

	let prizes_options = [{ k:0, v:i18n("No prize") }];
	let prize_to_win_image = false;
	switch(stage.reveal_mode) {
		case 'win':
			state.reveal_win.map((i) => {
				if(i.id != '0') {
					prizes_options.push({ k:'prize', v:i18n("Prize") });
				}
				if(i.id == preview) {
					if(i.full_scratch_image != undefined) {
						prize_to_win_image = i.full_scratch_image;
					} else {
						prize_to_win_image = (i.id == '0') ? `${default_images_path}chest_empty.png` : `${default_images_path}chest_prize.png`;
					}
				}
			});
			break;

		case 'prizes':
			state.scratch.map((i) => {
				if(i.id != '0') {
					const prize = props.prizes.find((p) => { return p.id == i.id; });
					prizes_options.push({ k:i.id, v:prize.title });
				}
				if(i.id == preview && i.full_scratch_image != undefined) prize_to_win_image = i.full_scratch_image;
			});
			break;
	}

	const _num_tries = getNumTries(stage.reveal_num, stage.reveal_size);
	const num_tries = stage?.reveal_tries || _num_tries[0].k;

	return (
		<>
			<BuilderSidebar>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props.nav_sections || []} 
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Select
						_class={{size:50, first:true}}
						value={preview}
						change={changePreview}
						name="reveal_num"
						values={prizes_options}
					>Game result</Select>

					<Message>
						<p>{i18n("Game mode")}: <strong>{i18n((stage.reveal_mode == 'win') ? "Simple: Win/Lose" : "Specific prizes: No prize item + an item per prize defined")}</strong></p>

						<p>{i18n("Attempts")}: <strong>{num_tries}</strong></p>

						{(prize_to_win_image !== false) && (
							<p style={{margin:'0.8em 0 0.5em'}}>
								<img src={prize_to_win_image} width={80} alt="" style={{border:'1px dashed'}} />
							</p>
						)}
					</Message>

					<ButtonCollection>
						<ActionButton
							icon='knk-reload'
							onClick={() => resetGame()}
						>Reset</ActionButton>
					</ButtonCollection>
				</article>
			</BuilderSidebar>

			{(preview == -1) ? (
				null
			) : (
				<RevealCanvasInBuilder
					change={change}
					stage={stage}
					state={state}
					promotion={props.promotion}
					prizes={props.prizes}
					promo_menu={props.promoModel.Custom__Menus}
					is_test={true}
					intl={props.intl}
					preview={preview}
				/>
			)}
		</>
	)
};

const RevealBuilder = ({ base_route, ...props }) => {
	const basename = `/stages/${props.stage.id}`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Gameplay settings", link:`${basename}/reveal`, icon:'knk-settings' },
		{ label:"Item images", link:`${basename}/images`, icon:'knk-picture' },
		{ label:"Design", link:`${basename}/design_reveal`, icon:'knk-artist-mode' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-settings' },
		{ label:"Test game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const stage = props.stage;

	const prizeIsAvailable = (p) => {
		if(!['4','5'].includes(p.assignation)) return false;
		if(p.stage_id == null) return true;
		if(p.stage_id == stage.id) return true;
		return false;
	};

	const generateWin = () => {
		if(stage.reveal_win) return stage.reveal_win;
		stage.reveal_win = [
			{ id:0, type:'reveal_win' },
			{ id:'prize', type:'reveal_win' },
		];
		return stage.reveal_win;
	};

	const generateScratch = () => {
		if(stage.scratch) {
			let present = stage.scratch.map((p) => p.id);

			const current_prizes = props.prizes.map((p) => p.id);
			stage.scratch = stage.scratch.filter((p) => {
				if(p.id == 0) return true;
				if(!current_prizes.includes(p.id)) return false;
				return true;
			});

			const reveal_no_win = stage.reveal_win.find(p=>p.id ==0);
			const no_prize =  reveal_no_win ? {...reveal_no_win,type:'reveal_prize'} :{ id:0, type:'reveal_prize' };

			if(!present.includes(0)) {
				present.push(0);
				stage.scratch = [...stage.scratch, no_prize];
			} else {
				stage.scratch.map(p=>{
					if (p.id != 0) return p;
					return no_prize;
				})
			}
			
			stage.scratch = stage.scratch.filter((p) => present.includes(p.id));

			props.prizes.filter(prizeIsAvailable).map((p) => {
				if(present.includes(p.id)) return;
				stage.scratch.unshift({ id:p.id, type:'reveal_prize' });
			});

			return stage.scratch;
		};

		const prizes_in_scratch = [...props.prizes.filter(prizeIsAvailable).map((p) => ({ id:p.id, type:'scratch_prize' })), { id:0, type:'scratch_prize' }];
		return prizes_in_scratch;
	};
	
	const [state, dispatch] = useReducer((state, action) => {
		switch(action.type) {
			case 'CHANGE_REVEAL_WIN':
				return { ...state, reveal_win:[...action.v] };

			case 'CHANGE_REVEAL_PRIZES':
				return { ...state, scratch:[...action.v] };

			default:
				return state;
		}
	}, {
		reveal_win:generateWin(),
		scratch:generateScratch(),
	});

	if(props.prizes.length < 1) {
		return (
			<BuilderMainWithoutSidebar pm={props.promoModel} has_tabs={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					sections={[{ label:"Go back", link:`/stages/${props.stage.id}/options`, icon:'knk-go-prev' }]}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<section class="not_awesome">
					<div>
						<div class="gui_section_header alone_section_header cleared_header">
							<SectionTitle _class={{other:'in_top'}}>No prize defined</SectionTitle>

							<CreateButton
								_href="#prizes"
							>Go to Prize Editor and Create my first prize</CreateButton>
						</div>

						<SectionSubtitle>You must create at least one prize before you configure the game.</SectionSubtitle>
					</div>
				</section>
			</BuilderMainWithoutSidebar>
		)
	}

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/reveal">
						<RevealPrizes
							change={props.change}
							intl={props.intl}
							nav_active={`/reveal`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={stage}
							state={state}
							dispatch={dispatch}
							on_save={props.on_save}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/images">
						<RevealImages
							change={props.change}
							intl={props.intl}
							nav_active={`/images`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							dispatch={dispatch}
							stage={stage}
							state={state}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/design_reveal">
						<RevealDesign
							change={props.change}
							intl={props.intl}
							nav_active={`/design_reveal`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={stage}
							state={state}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<RevealInstructions
							change={props.change}
							intl={props.intl}
							nav_active={`/instructions`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={stage}
							state={state}
							stage_indicator={props.stage_indicator}
						/>
					</Route>

					<Route exact={true} path="/test">
						<RevealTest
							change={props.change}
							intl={props.intl}
							nav_active={`/test`}
							nav_sections={nav_sections}
							promotion={props.promotion}
							promoModel={props.promoModel}
							instantStore={props.instantStore}
							prizes={props.prizes}
							saveStage={props.saveStage}
							winnerStore={props.winnerStore}
							stage={stage}
							state={state}
							stage_indicator={props.stage_indicator}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
}
export default injectIntl(RevealBuilder);